// This component renders a dynamic list section in the resume editor,
// allowing users to add, remove, and edit items with configurable fields.
// It supports formatting options for individual fields.[italics/bold/underline/list].


import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { FormattingResume } from "./FormattingResume";
import { X, DiamondPlus } from 'lucide-react'
import { useTranslation} from "react-i18next";
import {AutoTranslate} from "@/lib/autoTranslate.tsx";

interface FieldConfig {
    name: string;
    label: string;
    type?: "text" | "number";
    component?: "input" | "textarea" | "select";
    options?: string[];        // for select dropdowns
    row?: number;              // group fields in the same row
    formattingOption?: boolean; // whether to show formatting options default to false
    formatting?: any | null;
    setFormatting?: (value: any) => void | null;
}

interface Props {
    title: string;
    items: any[];
    path: string;
    fields: FieldConfig[];
    onUpdate: (path: string, value: any) => void;
    onAdd: () => void;
    onRemove: (index: number) => void;
}

export function DynamicListSection({
    title,
    items,
    path,
    fields,
    onUpdate,
    onAdd,
    onRemove,
}: Props) {
    const T = useTranslation().t('dynamicListSection', { returnObjects: true })
    return (
        <div className="mt-10 pt-10">
            <div className="flex justify-center border-b mb-4 items-center">
                <h3 className="font-bold text-xl text-center mb-4 ">{title}</h3>
            </div>

            {items?.map((item, index) => (
                <div key={item.id ?? index} className="border p-2.5 rounded-3xl mb-4">

                    {/* Header */}
                    <div className="flex justify-between mb-2">
                        <Label className="font-semibold">
                            {title} #{index + 1}
                        </Label>
                        <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Remove item" onClick={() => onRemove(index)}>
                            <X className="h-6 w-6" />
                        </Button>
                    </div>

                    {/* Group fields by row */}
                    {Object.values(
                        fields.reduce((groups, field) => {
                            const row = field.row || 1;
                            if (!groups[row]) groups[row] = [];
                            groups[row].push(field);
                            return groups;
                        }, {} as Record<number, FieldConfig[]>)
                    ).map((group, i) => (
                        <div
                            key={i}
                            className={`grid gap-3 mb-3`}
                            style={{ gridTemplateColumns: `repeat(${group.length}, 1fr)` }}
                        >
                            {group.map((field) => {
                                const fieldPath = `${path}.${index}.${field.name}`;
                                const fieldId = `field-${index}-${field.name}`;

                                return (
                                    <div key={field.name}>
                                        <div className="mb-1 flex items-center justify-between">
                                            <Label htmlFor={fieldId}>{field.label}</Label>
                                            {field.formattingOption && (
                                                <FormattingResume
                                                    section={field.name}
                                                    formatting={field.formatting}
                                                    setFormatting={field.setFormatting}
                                                />
                                            )}
                                        </div>

                                        {field.component === "textarea" ? (
                                            <Textarea
                                                id={fieldId}
                                                value={item[field.name] || ""}
                                                onChange={(e) =>
                                                    onUpdate(fieldPath, e.target.value)
                                                }
                                            />
                                        ) : field.component === "select" ? (
                                            <Select
                                                value={item[field.name] || ""}
                                                onValueChange={(value) =>
                                                    onUpdate(fieldPath, value)
                                                }
                                            >
                                                <SelectTrigger id={fieldId}>
                                                    <SelectValue placeholder="Select..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {field.options?.map((opt) => (
                                                        <SelectItem key={opt} value={opt}>
                                                            {opt}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <Input
                                                id={fieldId}
                                                type={field.type || "text"}
                                                value={item[field.name] || ""}
                                                onChange={(e) =>
                                                    onUpdate(fieldPath, e.target.value)
                                                }
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            ))}

            <Button className="mt-2" onClick={onAdd}>
                <DiamondPlus className="h-5 w-5"
                />
                {AutoTranslate(`Add`)} {title}
            </Button>
        </div>
    );
}
